import { resolveErrorResponseMessage } from '@repo/ui/lib/error-messages'
import axios from 'axios'

/**
 * Mensagens pt-BR por código para os erros de negócio tratados pelo aplicativo.
 * Os detalhes específicos do backend têm preferência na exibição.
 */
export const ERROR_MESSAGES_PT: Record<string, string> = {
  // Cadências
  CADENCE_CHANNEL_STRATEGY_REQUIRED:
    'Defina a estratégia de canal antes de ativar a cadência.',
  CADENCE_CHANNEL_REQUIRED:
    'Selecione ao menos um canal para ativar a cadência.',
  ANY_AVAILABLE_CHANNEL_RISK_ACK_REQUIRED:
    "Confirme o aviso de risco do modo 'qualquer canal disponível' antes de ativar.",
  CADENCE_DELIVERY_WINDOW_REQUIRED:
    'Defina os dias e o horário de envio antes de ativar a cadência.',
  INVALID_TAGS: 'Uma ou mais tags não pertencem a esta organização.',
  CADENCE_NOT_ACTIVE: 'A cadência precisa estar ativa para esta ação.',
  NO_ENTRY_TAGS: 'Configure as tags de entrada da cadência primeiro.',
  CADENCE_HAS_NO_STEPS: 'Adicione ao menos uma etapa à cadência.',
  ENROLLMENT_NOT_RETRYABLE:
    'Só é possível reprocessar inscrições com falha ou canceladas.',
  ALREADY_ENROLLED:
    'Este lead já tem uma inscrição ativa ou pausada em uma cadência.',
  ALREADY_ENROLLED_ONCE:
    'Esta cadência é de disparo único e o lead já foi inscrito nela.',
  // Consentimento
  CONSENT_REQUIRED:
    'Este lead não tem consentimento registrado para marketing. Registre a evidência na tela do lead antes de enviar.',
  OPTED_OUT:
    'Este lead fez opt-out e não pode receber comunicações automáticas.',
  NOT_FOUND: 'Registro não encontrado.',
}

/**
 * Códigos cuja mensagem do backend já vem pronta em pt-BR e é ESPECÍFICA daquela
 * ocorrência (ex.: o motivo exato da rejeição da Meta, qual botão está inválido).
 * Traduzir por um texto fixo aqui perderia essa informação — então exibimos a
 * mensagem do servidor, mas continuam contando como erro tratado (fora do Sentry).
 */
const PASSTHROUGH_ERROR_CODES = new Set([
  'META_TEMPLATE_REJECTED',
  'INVALID_BUTTON',
  'INVALID_BODY',
  'INVALID_TEMPLATE_NAME',
  'ALREADY_SUBMITTED',
  'NOT_ON_META',
])

/** Extrai o `error` code do corpo da resposta de um AxiosError, se houver. */
function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined
  const code = (error.response?.data as { error?: unknown } | undefined)?.error
  return typeof code === 'string' ? code : undefined
}

/**
 * Erro "esperado/tratado": AxiosError cujo `error` code o front reconhece (tem
 * tradução ou mensagem pronta do backend). Usado para NÃO enviar ao Sentry.
 */
export function isHandledApiError(error: unknown): boolean {
  const code = getApiErrorCode(error)
  return Boolean(
    code && (code in ERROR_MESSAGES_PT || PASSTHROUGH_ERROR_CODES.has(code))
  )
}

/** Mensagem amigável (pt-BR) para exibir ao usuário a partir de um erro de API. */
export function resolveApiErrorMessage(
  error: unknown,
  fallback = 'Erro inesperado'
): string {
  const code = getApiErrorCode(error)

  if (axios.isAxiosError(error)) {
    // Sem resposta do servidor: rede caiu, CORS ou timeout. Nunca usar o
    // `error.message` do axios, que é en-US ("Network Error", "timeout of…").
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message)) {
        return 'A requisição demorou demais. Tente novamente.'
      }
      return 'Falha de conexão. Verifique sua internet e tente novamente.'
    }

    const businessMessage = code && ERROR_MESSAGES_PT[code]
    if (businessMessage && !error.response.data?.message) return businessMessage

    return resolveErrorResponseMessage(
      error.response.data,
      error.response.status,
      (code && ERROR_MESSAGES_PT[code]) || fallback
    )
  }

  return fallback
}
